/**
 * Synkprotokoll mellan klient och AutoDay-server (delas av båda).
 */
import type { PlanCommitRequest, PlanStaleReason } from '../prepPlans.ts';

/** Ursprungliga posttyper. Äldre klienter känner bara till dessa och får aldrig andra från servern. */
export type LegacySyncEntity = 'event' | 'overlay' | 'template' | 'sourceItem' | 'importSource' | 'meta';
/**
 * Förberedelseplanernas posttyper. Lagras i en egen tabell på servern och skickas bara till klienter som
 * uttryckligen ber om dem (pull med prepSince). En äldre klients applyRemote har ingen hantering av
 * okända posttyper – därför får den aldrig se dem.
 */
export type PrepSyncEntity = 'prepTemplate' | 'prepRule' | 'prepPlan';
export type SyncEntity = LegacySyncEntity | PrepSyncEntity;

export const LEGACY_SYNC_ENTITIES: readonly LegacySyncEntity[] = ['event', 'overlay', 'template', 'sourceItem', 'importSource', 'meta'];
export const PREP_SYNC_ENTITIES: readonly PrepSyncEntity[] = ['prepTemplate', 'prepRule', 'prepPlan'];
export function isPrepEntity(e: string): e is PrepSyncEntity {
  return (PREP_SYNC_ENTITIES as readonly string[]).includes(e);
}

/** Serverns stöd för förberedelseplaner (GET /api/config → features.prepPlans). */
export const PREP_PROTOCOL_VERSION = 1;

export interface PushOp {
  opId: string;
  entity: SyncEntity;
  id: string;
  /** Serverns revision som ändringen bygger på (null = ny post). */
  baseRevision: number | null;
  doc: unknown;
  deleted?: boolean;
  /** För sourceItem: ändringen kommer från denna import (krävs för att datum ska få ändras). */
  importId?: string;
}

export type PushResult =
  | { opId: string; status: 'applied'; revision: number }
  | { opId: string; status: 'duplicate'; revision: number }
  | { opId: string; status: 'converged'; revision: number }
  | { opId: string; status: 'conflict'; serverRevision: number; serverDoc: unknown; serverDeleted: boolean }
  | { opId: string; status: 'rejected'; code: string; message: string };

export interface PullChange {
  entity: SyncEntity;
  id: string;
  revision: number;
  doc: unknown;
  deleted: boolean;
  seq: number;
}

export interface PullResponse {
  cursor: number;
  changes: PullChange[];
  more: boolean;
  /**
   * Förberedelseplaner – egen ström med EGEN markör (prepSince/prep.cursor). Finns bara när klienten bad
   * om den och servern stöder den. En klient som får stöd senare börjar från 0 och kan därför aldrig ha
   * passerat äldre plandata osynligt med den vanliga markören.
   */
  prep?: { cursor: number; changes: PullChange[]; more: boolean };
}

// ---------------------------------------------------------------------------
// Atomiskt plangodkännande (konto): POST /api/prep/commit och /api/prep/commit/resolve
// ---------------------------------------------------------------------------

export interface PlanCommitBody {
  deviceId: string;
  request: PlanCommitRequest;
}

export interface PlanCommitDoc {
  entity: 'event' | 'prepPlan';
  id: string;
  /** Serverns radrevision efter skrivningen. */
  revision: number;
  doc: unknown;
  deleted: boolean;
}

export type PlanCommitResponse =
  /** Allt skrevs i en transaktion. duplicate = samma opId redan tillämpat (svaret hämtas igen). */
  | { status: 'applied' | 'duplicate'; opId: string; planId: string; docs: PlanCommitDoc[] }
  /** Import: planen fanns redan i kontot – ingenting skrevs. */
  | { status: 'skipped'; opId: string; planId: string; reason: 'plan-exists' }
  /** Underlaget har ändrats – ingenting skrevs. */
  | { status: 'stale'; opId: string; reason: PlanStaleReason; message: string }
  /** Operationen förklarades obekräftad (resolve) innan den nådde fram – den tillämpas aldrig. */
  | { status: 'abandoned'; opId: string };

export type PlanResolveResponse = PlanCommitResponse | { status: 'not-applied'; opId: string };
